/**
 * Drop tables, edited against what a hundred kills would look like.
 *
 * Split out from the rest of the details panel because it is the one part of a
 * character that is a table rather than a handful of numbers, and because it is
 * the piece combat will read once there is anything to kill.
 */

import type { DropTablePacket, ItemDefinitionWire, QuestWire } from '@inverse/protocol';
import { useState } from 'react';
import type { AdminCommand } from '../Admin';
import { Conditions } from './Dialogue';
import { Explain } from '../Explain';
import { hundredKills, oneIn, rollsOf, rolledWeight, type Roll } from './drops';

/**
 * What a character drops, and what a hundred kills of it would look like.
 *
 * The preview is the point. A weight of forty means nothing on its own, so the
 * panel says how many kills in a hundred give the line and how much it hands over
 * when it does.
 */
/** What a table came to when the world turned it many times over. */
export interface DropTest {
  npcId: number;
  kills: number;
  nothing: number;
  items: { itemId: number; kills: number; total: number }[];
}

export function DropEditor({
  npcId,
  drops,
  test,
  items,
  quests,
  characters,
  objects,
  onCommand,
}: {
  npcId: number;
  drops: DropTablePacket | null;
  /** The last time the table was tried, if it was this one. */
  test: DropTest | null;
  items: readonly ItemDefinitionWire[];
  /** For a roll that only turns for somebody partway through a quest. */
  quests: readonly QuestWire[];
  characters: readonly { id: number; name: string }[];
  objects: readonly { id: number; name: string }[];
  onCommand: AdminCommand;
}): JSX.Element {
  const [search, setSearch] = useState('');
  const [addTo, setAddTo] = useState<number | null>(null);
  const [rollSearch, setRollSearch] = useState('');
  const [open, setOpen] = useState<number | null>(null);
  const [amount, setAmount] = useState('1');
  const [weight, setWeight] = useState('10');

  const table = drops && drops.npcId === npcId ? drops : null;
  const entries = table?.entries ?? [];

  const nameOf = (itemId: number): string =>
    items.find((item) => item.id === itemId)?.name ?? `Item ${itemId}`;

  const preview = hundredKills(entries, nameOf);

  // The rolls as they stand: whatever has been typed since the last answer
  // came back, and the server's own word once it has. A reply reseeds the
  // panel, so the thing on screen is never a guess about what was saved.
  const seed = table?.rolls ?? '[]';
  const [edited, setEdited] = useState<{ from: string; rolls: Roll[] } | null>(null);
  const rolls = edited && edited.from === seed ? edited.rolls : rollsOf(seed);

  const push = (next: Roll[], send: boolean): void => {
    setEdited({ from: seed, rolls: next });
    if (send) onCommand('setRolls', { npc: npcId }, { json: JSON.stringify(next) });
  };

  const setRoll = (at: number, wanted: Roll, send: boolean): void =>
    push(
      rolls.map((one, index) => (index === at ? wanted : one)),
      send,
    );

  const wanted = search.trim().toLowerCase();
  const matches = wanted
    ? items.filter((item) => item.name.toLowerCase().includes(wanted)).slice(0, 6)
    : [];

  /** Reads "1", "3 to 8" or "3-8" as the range it looks like. */
  const range = (text: string): { min: number; max: number } => {
    const numbers = text.match(/\d+/g)?.map(Number) ?? [1];
    const min = Math.max(1, numbers[0] ?? 1);
    return { min, max: Math.max(min, numbers[1] ?? min) };
  };

  const add = (itemId: number, everyKill: boolean): void => {
    const { min, max } = range(amount);
    onCommand('setDrop', {
      npc: npcId,
      slot: -1,
      item: itemId,
      min,
      max,
      weight: Math.max(1, Number(weight) || 1),
      everyKill: everyKill ? 1 : 0,
    });
    setSearch('');
  };

  return (
    <>
      <h3>Drops</h3>

      {!table ? <p className="hint">Asking the server what it drops.</p> : null}

      {table && entries.length === 0 ? <p className="hint">It drops nothing at all.</p> : null}

      {preview.map((line, slot) => (
        <div className="editor-drop" key={`${line.name}-${slot}`}>
          <span className="editor-drop-name">{line.name}</span>
          <span className="editor-drop-count">{line.amount}</span>
          <span className="editor-drop-times">
            {line.everyKill ? 'every kill' : `${line.times} in 100`}
          </span>
          <button
            type="button"
            className="editor-drop-remove"
            title="Delete this line"
            onClick={() => onCommand('removeDrop', { npc: npcId, slot })}
          >
            ✕
          </button>
        </div>
      ))}

      <div className="editor-drop-add">
        <input
          className="editor-search"
          type="text"
          value={search}
          placeholder="Add an item"
          onChange={(event) => setSearch(event.target.value)}
        />
        <div className="editor-drop-fields">
          <label>
            <span>Quantity</span>
            <input type="text" value={amount} onChange={(event) => setAmount(event.target.value)} />
          </label>
          <label>
            <span>Weight</span>
            <input type="text" value={weight} onChange={(event) => setWeight(event.target.value)} />
          </label>
        </div>

        {matches.map((item) => (
          <div className="editor-drop-match" key={item.id}>
            <span>{item.name}</span>
            <button type="button" onClick={() => add(item.id, false)}>
              Rolled
            </button>
            <button type="button" onClick={() => add(item.id, true)}>
              Every kill
            </button>
          </div>
        ))}

        {table && rolledWeight(entries) > 0 ? (
          <button type="button" className="wide" onClick={() => add(0, false)}>
            Add a chance of nothing
          </button>
        ) : null}

        <button type="button" className="wide" onClick={() => onCommand('saveDrops')}>
          Save the drops
        </button>
      </div>

      <h4>Further rolls</h4>
      <p className="hint">
        Each one is turned on its own, after the table above.
        <Explain>
          This is what a rare drop is: a roll of its own, with long odds, that leaves the ordinary
          table alone. Making something rarer here never quietly makes everything else commoner.
          Conditions hold a roll back until whoever made the kill has earned it.
        </Explain>
      </p>

      {rolls.map((roll, at) => (
        <div className="editor-act" key={at}>
          <div className="editor-brushes">
            <input
              type="text"
              value={roll.name}
              placeholder="What to call it"
              onChange={(event) => setRoll(at, { ...roll, name: event.target.value }, false)}
              onBlur={() => push(rolls, true)}
            />
            <label className="editor-inline">
              <span>One in</span>
              <input
                type="text"
                value={String(roll.chance ?? 1)}
                onChange={(event) =>
                  setRoll(
                    at,
                    { ...roll, chance: Math.max(1, Math.round(Number(event.target.value) || 1)) },
                    false,
                  )
                }
                onBlur={() => push(rolls, true)}
              />
            </label>
            <button
              type="button"
              className={open === at ? 'editor-tab on' : 'editor-tab'}
              title="What has to be true of whoever made the kill"
              onClick={() => setOpen(open === at ? null : at)}
            >
              {roll.when.length > 0 ? `Earned (${roll.when.length})` : 'Anybody'}
            </button>
            <button
              type="button"
              className="editor-drop-remove"
              title="Delete this roll"
              onClick={() =>
                push(
                  rolls.filter((_, index) => index !== at),
                  true,
                )
              }
            >
              ✕
            </button>
          </div>

          {open === at ? (
            <Conditions
              held={roll.when}
              items={items}
              quests={quests}
              characters={characters}
              objects={objects}
              onChange={(when) => setRoll(at, { ...roll, when }, true)}
            />
          ) : null}

          {roll.entries.map((entry, slot) => (
            <div className="editor-drop" key={slot}>
              <span className="editor-drop-name">
                {entry.itemId && entry.itemId > 0 ? nameOf(entry.itemId) : 'Nothing'}
              </span>
              <span className="editor-drop-count">
                {entry.min === entry.max ? entry.min : `${entry.min} to ${entry.max}`}
              </span>
              <span className="editor-drop-times">1 in {oneIn(roll, entry)}</span>
              <button
                type="button"
                className="editor-drop-remove"
                title="Delete this line"
                onClick={() =>
                  setRoll(
                    at,
                    { ...roll, entries: roll.entries.filter((_, index) => index !== slot) },
                    true,
                  )
                }
              >
                ✕
              </button>
            </div>
          ))}

          {addTo === at ? (
            <div className="editor-drop-add">
              <input
                className="editor-search"
                type="text"
                value={rollSearch}
                placeholder="Add an item"
                onChange={(event) => setRollSearch(event.target.value)}
              />
              {items
                .filter(
                  (item) =>
                    rollSearch.trim().length > 0 &&
                    item.name.toLowerCase().includes(rollSearch.trim().toLowerCase()),
                )
                .slice(0, 6)
                .map((item) => (
                  <div className="editor-drop-match" key={item.id}>
                    <span>{item.name}</span>
                    <button
                      type="button"
                      onClick={() => {
                        const { min, max } = range(amount);
                        setRoll(
                          at,
                          {
                            ...roll,
                            entries: [
                              ...roll.entries,
                              {
                                itemId: item.id,
                                min,
                                max,
                                weight: Math.max(1, Number(weight) || 1),
                              },
                            ],
                          },
                          true,
                        );
                        setRollSearch('');
                        setAddTo(null);
                      }}
                    >
                      Add
                    </button>
                  </div>
                ))}
              <button
                type="button"
                className="wide"
                onClick={() => {
                  setRoll(
                    at,
                    {
                      ...roll,
                      entries: [
                        ...roll.entries,
                        { itemId: null, min: 1, max: 1, weight: Math.max(1, Number(weight) || 1) },
                      ],
                    },
                    true,
                  );
                  setAddTo(null);
                }}
              >
                Add a chance of nothing
              </button>
            </div>
          ) : (
            <button type="button" className="wide" onClick={() => setAddTo(at)}>
              Add a line to this roll
            </button>
          )}
        </div>
      ))}

      <button
        type="button"
        className="wide"
        onClick={() => push([...rolls, { name: 'Rare', chance: 128, when: [], entries: [] }], true)}
      >
        Add a roll
      </button>

      <h4>Try it</h4>
      <p className="hint">
        The whole table, further rolls and all, turned by the world as many times as asked.
        <Explain>
          Conditions are judged as they stand for you now, so a roll held back for a quest you have
          not reached gives nothing here either. Trying it changes nothing about real kills.
        </Explain>
      </p>
      <div className="editor-brushes">
        {[100, 1000, 10000].map((kills) => (
          <button
            key={kills}
            type="button"
            className="editor-tab"
            onClick={() => onCommand('testDrops', { npc: npcId, kills })}
          >
            {kills.toLocaleString()} kills
          </button>
        ))}
      </div>
      {test && test.npcId === npcId ? (
        <div className="drop-test">
          <p className="hint">
            {test.kills.toLocaleString()} kills.{' '}
            {test.nothing > 0
              ? `${test.nothing.toLocaleString()} gave nothing at all (${Math.round((test.nothing / test.kills) * 100)}%).`
              : 'Every one gave something.'}
          </p>
          <table>
            <thead>
              <tr>
                <th>Item</th>
                <th>Kills that gave it</th>
                <th>About</th>
                <th>Each time</th>
              </tr>
            </thead>
            <tbody>
              {test.items.map((one) => (
                <tr key={one.itemId}>
                  <td>{nameOf(one.itemId)}</td>
                  <td>{one.kills.toLocaleString()}</td>
                  <td>
                    {one.kills >= test.kills
                      ? 'every kill'
                      : `1 in ${Math.max(1, Math.round(test.kills / one.kills)).toLocaleString()}`}
                  </td>
                  <td>{(one.total / Math.max(1, one.kills)).toFixed(1)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </>
  );
}
